import React, { useState, useEffect, useMemo } from 'react';
import radiologyMasterConfigService from '../../services/radiologyMasterConfigService';
import './RadiologySmartPhraseMaster.css';

const CATEGORIES = ['ALL', 'Brain', 'Chest', 'Abdomen', 'Spine', 'Musculoskeletal', 'General'];

export default function RadiologySmartPhraseMaster() {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Add / Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingPhrase, setEditingPhrase] = useState(null); // null = new
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    shortcut: '',
    title: '',
    category: 'General',
    phraseText: '',
    scope: 'GLOBAL',
    isActive: true
  });

  // Clear messages
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const fetchPhrases = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await radiologyMasterConfigService.getSmartPhrases();
      if (res.success && Array.isArray(res.data)) {
        setPhrases(res.data);
      } else {
        setPhrases([]);
      }
    } catch (err) {
      console.error('Failed to load smart phrases:', err);
      setErrorMsg('Failed to load clinical smart phrases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhrases();
  }, []);

  // Filtered list
  const filteredPhrases = useMemo(() => {
    return phrases.filter((p) => {
      const matchesCat =
        selectedCategory === 'ALL' ||
        (p.category || '').toLowerCase() === selectedCategory.toLowerCase();

      if (!matchesCat) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (p.shortcut || '').toLowerCase().includes(q) ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.phraseText || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    });
  }, [phrases, selectedCategory, searchQuery]);

  const handleOpenCreate = () => {
    setEditingPhrase(null);
    setForm({
      shortcut: ';',
      title: '',
      category: selectedCategory === 'ALL' ? 'General' : selectedCategory,
      phraseText: '',
      scope: 'GLOBAL',
      isActive: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (phrase) => {
    setEditingPhrase(phrase);
    setForm({
      shortcut: phrase.shortcut,
      title: phrase.title || '',
      category: phrase.category || 'General',
      phraseText: phrase.phraseText || '',
      scope: phrase.scope || 'GLOBAL',
      isActive: phrase.isActive !== false
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.shortcut.trim()) {
      setErrorMsg('Shortcut trigger is required (e.g. ;normalbrain)');
      return;
    }
    if (!form.phraseText.trim()) {
      setErrorMsg('Phrase text expansion content cannot be empty.');
      return;
    }

    setSaving(true);
    setErrorMsg(null);
    try {
      const payload = {
        shortcut: form.shortcut.trim(),
        title: form.title.trim() || form.shortcut.trim(),
        category: form.category,
        phraseText: form.phraseText,
        scope: form.scope,
        isActive: form.isActive
      };

      if (editingPhrase) {
        const res = await radiologyMasterConfigService.updateSmartPhrase(
          editingPhrase.smartPhraseId,
          payload
        );
        if (res.success) {
          setSuccessMsg(`Updated smart phrase "${form.shortcut}".`);
          setShowModal(false);
          fetchPhrases();
        }
      } else {
        const res = await radiologyMasterConfigService.createSmartPhrase(payload);
        if (res.success) {
          setSuccessMsg(`Added new smart phrase "${form.shortcut}".`);
          setShowModal(false);
          fetchPhrases();
        }
      }
    } catch (err) {
      console.error('Failed to save smart phrase:', err);
      setErrorMsg(err.response?.data?.message || 'Error saving smart phrase.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (phrase) => {
    if (!window.confirm(`Delete smart phrase "${phrase.shortcut}"?`)) return;
    try {
      const res = await radiologyMasterConfigService.deleteSmartPhrase(phrase.smartPhraseId);
      if (res.success) {
        setSuccessMsg(`Deleted smart phrase "${phrase.shortcut}".`);
        fetchPhrases();
      }
    } catch (err) {
      console.error('Failed to delete smart phrase:', err);
      setErrorMsg('Failed to delete smart phrase.');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
    setSuccessMsg(`Copied shortcut "${text}" to clipboard!`);
  };

  return (
    <div className="rad-sp-master-container">
      {/* Header Bar */}
      <div className="rad-sp-header-bar">
        <div>
          <h2>💬 Clinical Smart Phrase & Macro Master</h2>
          <p>
            Configure reusable diagnostic paragraphs and macro shortcuts (e.g.{' '}
            <code>;normalbrain</code>, <code>;normalchest</code>) that radiologists can insert instantly.
          </p>
        </div>
        <button type="button" className="rad-sp-btn-create" onClick={handleOpenCreate}>
          + Add Smart Phrase
        </button>
      </div>

      {/* Notifications */}
      {errorMsg && (
        <div className="rad-sp-alert error">
          <span>⚠️ {errorMsg}</span>
          <button onClick={() => setErrorMsg(null)}>✕</button>
        </div>
      )}
      {successMsg && (
        <div className="rad-sp-alert success">
          <span>✓ {successMsg}</span>
          <button onClick={() => setSuccessMsg(null)}>✕</button>
        </div>
      )}

      {/* Controls Bar: Category Filter & Search */}
      <div className="rad-sp-filter-row">
        <div className="rad-sp-cats">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`rad-sp-cat-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="rad-sp-search-field">
          <span>🔍</span>
          <input
            type="text"
            placeholder="Search shortcut, title, or clinical text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="rad-sp-clear" onClick={() => setSearchQuery('')}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Grid of Smart Phrases */}
      {loading ? (
        <div className="rad-sp-loading">
          <div className="rad-spinner"></div>
          <p>Loading smart phrases...</p>
        </div>
      ) : filteredPhrases.length === 0 ? (
        <div className="rad-sp-empty-state">
          <div className="rad-sp-empty-icon">💬</div>
          <h3>No Smart Phrases Found</h3>
          <p>No shortcuts match your current filter or search criteria.</p>
          <button type="button" className="rad-sp-btn-create" onClick={handleOpenCreate}>
            + Add First Smart Phrase
          </button>
        </div>
      ) : (
        <div className="rad-sp-cards-grid">
          {filteredPhrases.map((phrase) => (
            <div key={phrase.smartPhraseId} className="rad-sp-card">
              <div className="rad-sp-card-header">
                <div className="rad-sp-shortcut-wrap">
                  <span
                    className="rad-sp-shortcut-tag"
                    onClick={() => handleCopy(phrase.shortcut)}
                    title="Click to copy shortcut"
                  >
                    {phrase.shortcut}
                  </span>
                  <span className="rad-sp-category-pill">{phrase.category || 'General'}</span>
                </div>
                <div className="rad-sp-actions">
                  <button
                    type="button"
                    className="rad-sp-btn-action"
                    onClick={() => handleOpenEdit(phrase)}
                    title="Edit smart phrase"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    className="rad-sp-btn-action delete"
                    onClick={() => handleDelete(phrase)}
                    title="Delete smart phrase"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <h4 className="rad-sp-title">{phrase.title || phrase.shortcut}</h4>

              <div className="rad-sp-preview-box">
                {phrase.phraseText}
              </div>

              <div className="rad-sp-card-footer">
                <span className="rad-sp-scope">{phrase.scope || 'GLOBAL'}</span>
                <button
                  type="button"
                  className="rad-sp-btn-copy"
                  onClick={() => handleCopy(phrase.phraseText)}
                  title="Copy full expansion text"
                >
                  📋 Copy Text
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Create or Edit */}
      {showModal && (
        <div className="rad-sp-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="rad-sp-modal-window" onClick={(e) => e.stopPropagation()}>
            <div className="rad-sp-modal-header">
              <h3>{editingPhrase ? 'Edit Smart Phrase' : 'Create New Smart Phrase'}</h3>
              <button
                type="button"
                className="rad-sp-modal-close"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="rad-sp-form">
              <div className="rad-sp-form-row">
                <div className="rad-sp-form-group">
                  <label>
                    Shortcut Trigger <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. ;normalbrain or .normlung"
                    value={form.shortcut}
                    onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
                    required
                    autoFocus
                  />
                  <small>Must start with a symbol like <code>;</code> or <code>.</code> for fast keyboard trigger.</small>
                </div>

                <div className="rad-sp-form-group">
                  <label>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {CATEGORIES.filter((c) => c !== 'ALL').map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rad-sp-form-group">
                <label>Descriptive Title</label>
                <input
                  type="text"
                  placeholder="e.g. Normal MRI Brain Findings"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="rad-sp-form-group">
                <label>
                  Expanded Clinical Text <span className="req">*</span>
                </label>
                <textarea
                  rows={6}
                  placeholder="Enter the full diagnostic text that will replace this shortcut..."
                  value={form.phraseText}
                  onChange={(e) => setForm({ ...form, phraseText: e.target.value })}
                  required
                ></textarea>
              </div>

              <div className="rad-sp-modal-footer">
                <button
                  type="button"
                  className="rad-btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rad-sp-btn-save"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingPhrase ? 'Update Smart Phrase' : 'Save Smart Phrase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
